import { AxiosRequestConfig } from "axios";
import { v4 as uuid } from "uuid";

import { createApiMutationHook, IApiMutationFunction } from "../../utils/api";
import { IMediaResource, IMediaSecuredResource, MediaType } from "../types";

// Base parameters
type BaseMediaCreateParameters = {
    uuid?: string;
    title?: string;
    onProgress?: (progress: number) => void;
};

// Secured media upload (file only)
type SecuredMediaCreateParameters = BaseMediaCreateParameters & {
    secured: true;
    file: File;
};

// Unsecured media upload (file or URL)
type UnsecuredFileCreateParameters = BaseMediaCreateParameters & {
    secured?: false;
    type: MediaType.FILE;
    file: File;
};

type UnsecuredUrlCreateParameters = BaseMediaCreateParameters & {
    secured?: false;
    type: MediaType.URL;
    url: string;
};

export type IMediaCreateMutationParameters =
    SecuredMediaCreateParameters | UnsecuredFileCreateParameters | UnsecuredUrlCreateParameters;

export type IMediaUploadMutationResult = IMediaResource | IMediaSecuredResource;

export const mediaCreateMutation: IApiMutationFunction<IMediaCreateMutationParameters> = params => {
    const { uuid: providedUuid, title, onProgress, secured } = params;

    const url = secured ? "/media_secureds" : "/media";
    const formData = new FormData();

    formData.append("uuid", providedUuid ?? uuid());

    if (title) {
        formData.append("title", title);
    }

    if (params.secured) {
        // Secured upload: file only
        formData.append("file", params.file);
    } else {
        // Unsecured upload: file or URL
        if (params.type === MediaType.FILE) {
            formData.append("file", params.file);
            formData.append("type", MediaType.FILE);
        } else {
            formData.append("url", params.url);
            formData.append("type", MediaType.URL);
        }
    }

    const config: AxiosRequestConfig = {
        method: "POST",
        url,
        data: formData,
        headers: {
            "Content-Type": "multipart/form-data",
        },
    };

    // Add progress callback if provided
    if (onProgress) {
        config.onUploadProgress = progressEvent => {
            const { total, loaded } = progressEvent;
            if (total) {
                onProgress((loaded / total) * 100);
            }
        };
    }

    return config;
};

export const useMediaCreateMutation = createApiMutationHook<IMediaCreateMutationParameters, IMediaUploadMutationResult>(
    mediaCreateMutation,
);
